export class ThemeManager {
  constructor(devtools) {
    this.devtools = devtools;
    this.themes = new Map();
    this.currentTheme = 'default';
    this.customCSS = '';
    this.layouts = new Map();
    this.currentLayout = 'floating';
    
    this.initializeDefaultThemes();
    this.initializeDefaultLayouts();
  }

  initializeDefaultThemes() {
    // Default dark theme (current)
    this.registerTheme('default', {
      name: 'Default Dark',
      colors: {
        primary: '#00ff00',
        secondary: '#ff6b6b',
        accent: '#4fc3f7',
        background: 'rgba(0, 0, 0, 0.9)',
        surface: '#1a1a1a',
        border: '#333',
        text: '#ffffff',
        textSecondary: '#888',
        error: '#ff6b6b',
        warning: '#ffa500',
        success: '#00aa00',
        info: '#4fc3f7'
      },
      fonts: {
        primary: "'Courier New', monospace",
        size: '12px',
        pixelated: true
      },
      effects: {
        borderRadius: '4px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        transition: '0.2s ease'
      }
    });

    // Matrix theme
    this.registerTheme('matrix', {
      name: 'Matrix',
      colors: {
        primary: '#00ff41',
        secondary: '#ff0040',
        accent: '#00ffff',
        background: 'rgba(0, 0, 0, 0.95)',
        surface: '#001100',
        border: '#00ff41',
        text: '#00ff41',
        textSecondary: '#008f11',
        error: '#ff0040',
        warning: '#ffff00',
        success: '#00ff41',
        info: '#00ffff'
      },
      fonts: {
        primary: "'Courier New', monospace",
        size: '12px',
        pixelated: true
      },
      effects: {
        borderRadius: '0px',
        boxShadow: '0 0 20px rgba(0, 255, 65, 0.3)',
        transition: '0.1s ease',
        textShadow: '0 0 5px currentColor'
      }
    });

    // Cyberpunk theme
    this.registerTheme('cyberpunk', {
      name: 'Cyberpunk',
      colors: {
        primary: '#ff00ff',
        secondary: '#00ffff',
        accent: '#ffff00',
        background: 'rgba(20, 0, 40, 0.9)',
        surface: '#2a0a2a',
        border: '#ff00ff',
        text: '#ffffff',
        textSecondary: '#aa55aa',
        error: '#ff0080',
        warning: '#ff8000',
        success: '#80ff00',
        info: '#0080ff'
      },
      fonts: {
        primary: "'Orbitron', 'Courier New', monospace",
        size: '12px',
        pixelated: false
      },
      effects: {
        borderRadius: '8px',
        boxShadow: '0 0 30px rgba(255, 0, 255, 0.4)',
        transition: '0.3s ease',
        textShadow: '0 0 10px currentColor'
      }
    });

    // Retro terminal theme
    this.registerTheme('retro', {
      name: 'Retro Terminal',
      colors: {
        primary: '#00aa00',
        secondary: '#ffaa00',
        accent: '#ffffff',
        background: 'rgba(0, 20, 0, 0.95)',
        surface: '#001a00',
        border: '#00aa00',
        text: '#00ff00',
        textSecondary: '#008800',
        error: '#ff4444',
        warning: '#ffaa00',
        success: '#00aa00',
        info: '#44aaff'
      },
      fonts: {
        primary: "'VT323', 'Courier New', monospace",
        size: '14px',
        pixelated: true
      },
      effects: {
        borderRadius: '0px',
        boxShadow: 'inset 0 0 50px rgba(0, 255, 0, 0.1)',
        transition: '0s',
        textShadow: '0 0 3px currentColor'
      }
    });

    // Light theme
    this.registerTheme('light', {
      name: 'Light Mode',
      colors: {
        primary: '#2196f3',
        secondary: '#f44336',
        accent: '#ff9800',
        background: 'rgba(255, 255, 255, 0.95)',
        surface: '#f5f5f5',
        border: '#ddd',
        text: '#333333',
        textSecondary: '#666666',
        error: '#f44336',
        warning: '#ff9800',
        success: '#4caf50',
        info: '#2196f3'
      },
      fonts: {
        primary: "'Roboto Mono', 'Courier New', monospace",
        size: '12px',
        pixelated: false
      },
      effects: {
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        transition: '0.2s ease'
      }
    });
  }

  initializeDefaultLayouts() {
    // Floating windows (current)
    this.registerLayout('floating', {
      name: 'Floating Windows',
      windowStyle: {
        position: 'fixed',
        draggable: true,
        resizable: false
      },
      positioning: 'cascade',
      maxWindows: 10
    });

    // Docked sidebar
    this.registerLayout('sidebar', {
      name: 'Sidebar Dock',
      windowStyle: {
        position: 'fixed',
        right: '0',
        top: '0',
        width: '400px',
        height: '100vh',
        draggable: false,
        resizable: true
      },
      positioning: 'stack',
      maxWindows: 1
    });

    // Bottom panel
    this.registerLayout('panel', {
      name: 'Bottom Panel',
      windowStyle: {
        position: 'fixed',
        bottom: '0',
        left: '0',
        width: '100vw',
        height: '300px',
        draggable: false,
        resizable: true
      },
      positioning: 'tabs',
      maxWindows: 1
    });

    // Grid layout
    this.registerLayout('grid', {
      name: 'Grid Layout',
      windowStyle: {
        position: 'fixed',
        draggable: false,
        resizable: false
      },
      positioning: 'grid',
      maxWindows: 4,
      gridColumns: 2
    });
  }

  registerTheme(id, theme) {
    this.themes.set(id, {
      id,
      ...theme,
      custom: false
    });
  }

  registerLayout(id, layout) {
    this.layouts.set(id, {
      id,
      ...layout,
      custom: false
    });
  }

  applyTheme(themeId) {
    const theme = this.themes.get(themeId);
    if (!theme) {
      console.warn(`Theme ${themeId} not found`);
      return false;
    }

    this.currentTheme = themeId;
    this.updateCSS(theme);
    
    // Store preference
    localStorage.setItem('devtools-theme', themeId);
    
    this.devtools.apiGlue.triggerHook('theme:changed', { themeId, theme });
    return true;
  }

  applyLayout(layoutId) {
    const layout = this.layouts.get(layoutId);
    if (!layout) {
      console.warn(`Layout ${layoutId} not found`);
      return false;
    }

    this.currentLayout = layoutId;
    this.updateLayout(layout);
    
    // Store preference
    localStorage.setItem('devtools-layout', layoutId);
    
    this.devtools.apiGlue.triggerHook('layout:changed', { layoutId, layout });
    return true;
  }

  updateCSS(theme) {
    // Remove existing theme styles
    const existingStyle = document.getElementById('devtools-theme-style');
    if (existingStyle) {
      existingStyle.remove();
    }

    // Create new theme styles
    const style = document.createElement('style');
    style.id = 'devtools-theme-style';
    style.textContent = this.generateThemeCSS(theme);
    
    document.head.appendChild(style);
  }

  generateThemeCSS(theme) {
    const { colors, fonts, effects } = theme;
    
    return `
      .devtools-window {
        background: ${colors.background} !important;
        border: 1px solid ${colors.border} !important;
        color: ${colors.text} !important;
        font-family: ${fonts.primary} !important;
        font-size: ${fonts.size} !important;
        border-radius: ${effects.borderRadius} !important;
        box-shadow: ${effects.boxShadow} !important;
        transition: ${effects.transition} !important;
        ${fonts.pixelated ? 'image-rendering: pixelated !important;' : ''}
        ${effects.textShadow ? `text-shadow: ${effects.textShadow} !important;` : ''}
      }

      .devtools-header {
        background: ${colors.surface} !important;
        border-bottom: 1px solid ${colors.border} !important;
      }

      .devtools-title {
        color: ${colors.secondary} !important;
        ${effects.textShadow ? `text-shadow: ${effects.textShadow} !important;` : ''}
      }

      .devtools-btn {
        background: ${colors.surface} !important;
        border: 1px solid ${colors.border} !important;
        color: ${colors.text} !important;
        transition: ${effects.transition} !important;
      }

      .devtools-btn:hover {
        background: ${colors.border} !important;
      }

      .devtools-timestamp {
        color: ${colors.textSecondary} !important;
      }

      .message-cell {
        color: ${colors.error} !important;
      }

      .type-cell {
        color: ${colors.accent} !important;
      }

      .source-cell {
        color: ${colors.warning} !important;
      }

      .devtools-code {
        background: ${colors.surface} !important;
        border: 1px solid ${colors.border} !important;
      }

      .devtools-error-line {
        background: ${this.hexToRgba(colors.error, 0.2)} !important;
        border-left: 3px solid ${colors.error} !important;
      }

      .devtools-cause-item {
        background: ${this.hexToRgba(colors.error, 0.1)} !important;
        border-left: 3px solid ${colors.error} !important;
      }

      .devtools-suggestion-item {
        background: ${this.hexToRgba(colors.accent, 0.1)} !important;
        border-left: 3px solid ${colors.accent} !important;
      }

      .priority-critical {
        background: ${colors.error} !important;
      }

      .priority-high {
        background: ${colors.warning} !important;
      }

      .priority-medium {
        background: ${colors.info} !important;
      }

      .priority-low {
        background: ${colors.success} !important;
      }
    `;
  }

  updateLayout(layout) {
    // Apply layout-specific positioning logic
    const windows = Array.from(this.devtools.windowManager.windows.values());
    
    switch (layout.positioning) {
      case 'cascade':
        this.applyCascadeLayout(windows, layout);
        break;
      case 'stack':
        this.applyStackLayout(windows, layout);
        break;
      case 'tabs':
        this.applyTabsLayout(windows, layout);
        break;
      case 'grid':
        this.applyGridLayout(windows, layout);
        break;
    }
  }

  applyCascadeLayout(windows, layout) {
    windows.forEach((window, index) => {
      const offset = index * 30;
      window.element.style.left = (100 + offset) + 'px';
      window.element.style.top = (100 + offset) + 'px';
      window.element.style.position = 'fixed';
    });
  }

  applyStackLayout(windows, layout) {
    windows.forEach((window, index) => {
      window.element.style.right = '0px';
      window.element.style.top = (index * 50) + 'px';
      window.element.style.width = layout.windowStyle.width;
      window.element.style.position = 'fixed';
    });
  }

  applyTabsLayout(windows, layout) {
    // Create tab container if it doesn't exist
    let tabContainer = document.getElementById('devtools-tab-container');
    if (!tabContainer) {
      tabContainer = document.createElement('div');
      tabContainer.id = 'devtools-tab-container';
      tabContainer.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100vw;
        height: 300px;
        background: rgba(0, 0, 0, 0.9);
        border-top: 1px solid #333;
        z-index: 999999;
      `;
      document.body.appendChild(tabContainer);
    }

    // Move windows into tab container
    windows.forEach((window, index) => {
      window.element.style.position = 'absolute';
      window.element.style.top = '30px';
      window.element.style.left = '0';
      window.element.style.width = '100%';
      window.element.style.height = 'calc(100% - 30px)';
      window.element.style.display = index === 0 ? 'block' : 'none';
      
      tabContainer.appendChild(window.element);
    });
  }

  applyGridLayout(windows, layout) {
    const cols = layout.gridColumns || 2;
    const windowWidth = Math.floor(window.innerWidth / cols);
    const windowHeight = Math.floor(window.innerHeight / Math.ceil(windows.length / cols));

    windows.forEach((window, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      
      window.element.style.left = (col * windowWidth) + 'px';
      window.element.style.top = (row * windowHeight) + 'px';
      window.element.style.width = windowWidth + 'px';
      window.element.style.height = windowHeight + 'px';
      window.element.style.position = 'fixed';
    });
  }

  // Create custom theme
  createCustomTheme(name, baseThemeId = 'default') {
    const baseTheme = this.themes.get(baseThemeId);
    if (!baseTheme) {
      throw new Error(`Base theme ${baseThemeId} not found`);
    }

    const customId = `custom_${Date.now()}`;
    const customTheme = {
      ...JSON.parse(JSON.stringify(baseTheme)),
      id: customId,
      name,
      custom: true
    };

    this.themes.set(customId, customTheme);
    return customId;
  }

  // Export/Import themes
  exportTheme(themeId) {
    const theme = this.themes.get(themeId);
    if (!theme) return null;

    return JSON.stringify(theme, null, 2);
  }

  importTheme(themeData) {
    try {
      const theme = JSON.parse(themeData);
      const id = theme.id || `imported_${Date.now()}`;
      
      this.themes.set(id, {
        ...theme,
        id,
        custom: true
      });
      
      return id;
    } catch (error) {
      throw new Error('Invalid theme data');
    }
  }

  // Utility functions
  hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Get available themes and layouts
  getAvailableThemes() {
    return Array.from(this.themes.values());
  }

  getAvailableLayouts() {
    return Array.from(this.layouts.values());
  }

  // Load saved preferences
  loadPreferences() {
    const savedTheme = localStorage.getItem('devtools-theme');
    const savedLayout = localStorage.getItem('devtools-layout');

    if (savedTheme && this.themes.has(savedTheme)) {
      this.applyTheme(savedTheme);
    }

    if (savedLayout && this.layouts.has(savedLayout)) {
      this.applyLayout(savedLayout);
    }
  }
}